<template>
    <div class="newAlertModal">
        <StepByStep
            :steps="alertSteps"
            :validation-rules="stepValidationRules"
            @step-change="onStepChange"
            @finish="handleCreate"
        >
            <!-- Step 1: Seleção de Arquivos -->
            <template #step-0="{ nextStep, canNext }">
                <div class="step_content">
                    <div class="modal-section">
                        <h4>Selecionar Arquivos</h4>

                        <FileSelector
                            v-model:selected-documents="selectedDocuments"
                            :available-documents="availableDocuments"
                        />

                        <p class="help_text">Apenas arquivos com data de vencimento podem ter alertas</p>
                    </div>

                    <div class="step_actions">
                        <sw-button
                            name="Cancelar"
                            color="gray"
                            @click="handleCancel"
                        />
                        <sw-button
                            name="Continuar"
                            color="blue"
                            @click="nextStep"
                            :disabled="!canNext"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 2: Configurações do Alerta -->
            <template #step-1="{ nextStep, prevStep, canNext }">
                <div class="step_content">
                    <!-- Configuração única (um arquivo selecionado) -->
                    <template v-if="!hasMultipleDocuments">
                        <div class="modal-section">
                            <h4>Informações Básicas</h4>
                            <sw-input
                                v-model="alertName"
                                label="Nome do Alerta"
                                placeholder="Ex: Alertas de Documentos Importantes"
                                icon="pencil"
                            />
                        </div>

                        <div class="modal-section">
                            <h4>Avisar com antecedência de:</h4>
                            <div class="trigger_days">
                                <div
                                    v-for="day in availableDays"
                                    :key="day"
                                    class="trigger_day"
                                    :class="{ active: triggerDays.includes(day) }"
                                    @click="toggleDay(day)"
                                >
                                    <span class="day_number">{{ day }}</span>
                                    <span class="day_label">{{ day === 1 ? 'dia' : 'dias' }}</span>
                                </div>
                            </div>
                            <p class="help_text">Selecione os dias de antecedência para receber os alertas</p>
                        </div>

                        <div class="modal-section">
                            <h4>Horário para envio</h4>
                            <sw-input
                                v-model="preferredTime"
                                label="Horário de envio dos alertas"
                                icon="clock"
                                mask="##:##"
                                placeholder="09:00"
                            />
                            <p class="help_text">Horário em que os alertas serão enviados (formato: HH:MM)</p>
                        </div>
                    </template>

                    <!-- Configurações individuais (múltiplos arquivos) -->
                    <template v-else>
                        <div class="modal-section">
                            <h4>Configuração Individual dos Alertas</h4>
                            <p class="help_text" style="margin-bottom: 1.5em; text-align: left;">
                                Configure cada alerta individualmente. Cada arquivo terá seu próprio alerta com as configurações definidas abaixo.
                            </p>

                            <div
                                v-for="document in selectedDocumentsData"
                                :key="document.id"
                                class="document_config_card"
                            >
                                <div class="document_config_header">
                                    <div class="document_config_title">
                                        <span>{{ document.name }}</span>
                                    </div>
                                    <span class="document_config_expiry">{{ formatExpiryDate(document.expiryDate) }}</span>
                                </div>

                                <div v-if="alertConfigs[document.id]" class="document_config_body">
                                    <sw-input
                                        v-model="alertConfigs[document.id].name"
                                        label="Nome do Alerta"
                                        placeholder="Ex: Alerta - CNH"
                                        icon="pencil"
                                    />

                                    <div class="config_field">
                                        <label class="field_label">Avisar com antecedência de:</label>
                                        <div class="trigger_days trigger_days--compact">
                                            <div
                                                v-for="day in availableDays"
                                                :key="day"
                                                class="trigger_day"
                                                :class="{ active: alertConfigs[document.id].triggerDays.includes(day) }"
                                                @click="toggleDayForDocument(document.id, day)"
                                            >
                                                <span class="day_number">{{ day }}</span>
                                                <span class="day_label">{{ day === 1 ? 'dia' : 'dias' }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <sw-input
                                        v-model="alertConfigs[document.id].preferredTime"
                                        label="Horário para envio"
                                        icon="clock"
                                        mask="##:##"
                                        placeholder="09:00"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>

                    <div class="step_actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <sw-button
                            name="Continuar"
                            color="blue"
                            @click="nextStep"
                            :disabled="!canNext"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 3: Destinatários e Finalização -->
            <template #step-2="{ prevStep, canNext }">
                <div class="step_content">
                    <div class="modal-section">
                        <h4>Destinatários</h4>
                        <sw-tag
                            v-model="emails"
                            label="Emails para receber alertas"
                            placeholder="Digite um email e pressione Enter"
                            icon="mail"
                            validation-type="email"
                        />
                        <p class="help_text">Digite os emails que receberão as notificações (pressione Enter após cada email)</p>
                    </div>

                    <!-- Aviso sobre criação de múltiplos alertas -->
                    <div v-if="hasMultipleDocuments" class="modal-section">
                        <div class="multiple_alerts_warning">
                            <div class="warning_icon">
                                <sw-icon name="info" />
                            </div>
                            <div class="warning_content">
                                <strong>Atenção:</strong> Será criado um alerta separado para cada arquivo selecionado, totalizando <strong>{{ selectedDocuments.length }} alertas</strong>. Cada alerta terá suas próprias configurações conforme definido anteriormente.
                            </div>
                        </div>
                    </div>

                    <div class="modal-section">
                        <h4>Resumo {{ hasMultipleDocuments ? 'dos Alertas' : 'do Alerta' }}</h4>

                        <!-- Resumo único (um arquivo) -->
                        <template v-if="!hasMultipleDocuments">
                            <div class="summary_content">
                                <div class="summary_item">
                                    <span class="summary_label">Arquivos:</span>
                                    <span class="summary_value">{{ selectedDocuments.length }} selecionado(s)</span>
                                </div>
                                <div v-if="selectedDocumentsData.length > 0" class="summary_item">
                                    <span class="summary_label">Lista:</span>
                                    <div class="summary_value">
                                        <div v-for="doc in selectedDocumentsData" :key="doc.id" class="summary_document">
                                            {{ doc.name }} ({{ formatExpiryDate(doc.expiryDate) }})
                                        </div>
                                    </div>
                                </div>
                                <div class="summary_item">
                                    <span class="summary_label">Nome do Alerta:</span>
                                    <span class="summary_value">{{ alertName || 'Não informado' }}</span>
                                </div>
                                <div class="summary_item">
                                    <span class="summary_label">Avisar com:</span>
                                    <span class="summary_value">{{ formatTriggerDays() }}</span>
                                </div>
                                <div class="summary_item">
                                    <span class="summary_label">Horário:</span>
                                    <span class="summary_value">{{ preferredTime }}</span>
                                </div>
                                <div class="summary_item">
                                    <span class="summary_label">Emails:</span>
                                    <span class="summary_value">
                                        <span v-if="emails.length === 0">Nenhum email informado</span>
                                        <span v-else>{{ emails.length }} email(s): {{ emails.join(', ') }}</span>
                                    </span>
                                </div>
                            </div>
                        </template>

                        <!-- Resumo múltiplo (vários arquivos) -->
                        <template v-else>
                            <div
                                v-for="document in selectedDocumentsData"
                                :key="document.id"
                                class="alert_summary_card"
                            >
                                <div class="alert_summary_header">
                                    <div class="alert_summary_title">
                                        <span>{{ document.name }}</span>
                                    </div>
                                    <span class="alert_summary_expiry">{{ formatExpiryDate(document.expiryDate) }}</span>
                                </div>

                                <div class="alert_summary_body">
                                    <div class="alert_detail_item">
                                        <div class="alert_detail_label">
                                            <sw-icon name="tag" />
                                            <span>Nome do Alerta</span>
                                        </div>
                                        <div class="alert_detail_value">{{ alertConfigs[document.id]?.name || 'Não informado' }}</div>
                                    </div>
                                    <div class="alert_detail_item">
                                        <div class="alert_detail_label">
                                            <sw-icon name="calendar" />
                                            <span>Avisar com antecedência</span>
                                        </div>
                                        <div class="alert_detail_value">{{ formatTriggerDaysForDocument(document.id) }}</div>
                                    </div>
                                    <div class="alert_detail_item">
                                        <div class="alert_detail_label">
                                            <sw-icon name="clock" />
                                            <span>Horário de envio</span>
                                        </div>
                                        <div class="alert_detail_value">{{ alertConfigs[document.id]?.preferredTime || '09:00' }}</div>
                                    </div>
                                </div>
                            </div>

                            <!-- Informação comum dos emails -->
                            <div class="summary_content summary_content--emails">
                                <div class="summary_item">
                                    <span class="summary_label">Destinatários (para todos):</span>
                                    <span class="summary_value">
                                        <span v-if="emails.length === 0">Nenhum email informado</span>
                                        <span v-else>{{ emails.join(', ') }}</span>
                                    </span>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="step_actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <sw-button
                            :name="hasMultipleDocuments ? `Criar ${selectedDocuments.length} Alertas` : 'Criar Alerta'"
                            color="blue"
                            @click="handleCreate"
                            :disabled="!canNext"
                        />
                    </div>
                </div>
            </template>
        </StepByStep>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, watch } from 'vue';
    import FileSelector from '@Component/files/FileSelector.vue';
    import StepByStep from '@Component/steps/StepByStep.vue';
    import { mockAvailableDocuments } from '@/data/mockData';
    import { ALERT_STATUS } from '@/constants';

    // Emits
    const emit = defineEmits(['close', 'save']);

    // Estados
    const selectedDocuments = ref([]);
    const alertName = ref('');
    const triggerDays = ref([30, 15, 7, 3, 1]);
    const preferredTime = ref('09:00');
    const emails = ref([]);
    const availableDocuments = ref([]);
    const alertConfigs = ref({}); // Configurações individuais por arquivo { documentId: { name, triggerDays, preferredTime } }

    // Dados disponíveis
    const availableDays = ref([30, 15, 7, 3, 1]);

    // Steps do wizard
    const alertSteps = ref([
        {
            title: 'Arquivos',
            subtitle: 'Selecionar arquivos'
        },
        {
            title: 'Configuração',
            subtitle: 'Definir alertas'
        },
        {
            title: 'Finalização',
            subtitle: 'Revisar e criar'
        }
    ]);

    // Validações por step
    const stepValidationRules = computed(() => ({
        0: () => selectedDocuments.value.length > 0,
        1: () => {
            if (hasMultipleDocuments.value) {
                // Validar configurações individuais
                return selectedDocuments.value.every(docId => {
                    const config = alertConfigs.value[docId];
                    return config &&
                           config.name.trim() !== '' &&
                           config.triggerDays.length > 0 &&
                           isValidTime(config.preferredTime);
                });
            } else {
                // Validar configuração única
                return alertName.value.trim() !== '' &&
                       triggerDays.value.length > 0 &&
                       isValidTime(preferredTime.value);
            }
        },
        2: () => emails.value.length > 0 && emails.value.every(email => isValidEmail(email))
    }));

    // Computed
    const hasMultipleDocuments = computed(() => selectedDocuments.value.length > 1);

    const canCreate = computed(() => {
        return selectedDocuments.value.length > 0 &&
               alertName.value.trim() !== '' &&
               triggerDays.value.length > 0 &&
               isValidTime(preferredTime.value) &&
               emails.value.length > 0;
    });

    const selectedDocumentsData = computed(() => {
        return availableDocuments.value.filter(doc =>
            selectedDocuments.value.includes(doc.id)
        );
    });

    // Methods
    const initializeAlertConfig = (documentId) => {
        if (!alertConfigs.value[documentId]) {
            const document = availableDocuments.value.find(doc => doc.id === documentId);
            alertConfigs.value[documentId] = {
                name: document ? `Alerta - ${document.name}` : '',
                triggerDays: [30, 15, 7, 3, 1],
                preferredTime: '09:00'
            };
        }
    };

    const toggleDay = (day) => {
        const index = triggerDays.value.indexOf(day);
        if (index > -1) {
            triggerDays.value.splice(index, 1);
        } else {
            triggerDays.value.push(day);
            triggerDays.value.sort((a, b) => b - a);
        }
    };

    const toggleDayForDocument = (documentId, day) => {
        initializeAlertConfig(documentId);
        const config = alertConfigs.value[documentId];
        const index = config.triggerDays.indexOf(day);

        if (index > -1) {
            config.triggerDays.splice(index, 1);
        } else {
            config.triggerDays.push(day);
            config.triggerDays.sort((a, b) => b - a);
        }
    };

    const formatTriggerDays = () => {
        if (triggerDays.value.length === 0) return 'Não configurado';
        return triggerDays.value.map(days => `${days} dia${days > 1 ? 's' : ''}`).join(', ');
    };

    const formatTriggerDaysForDocument = (documentId) => {
        const config = alertConfigs.value[documentId];
        if (!config || config.triggerDays.length === 0) return 'Não configurado';
        return config.triggerDays.map(days => `${days} dia${days > 1 ? 's' : ''}`).join(', ');
    };

    const formatExpiryDate = (expiryDate) => {
        if (!expiryDate) return 'Sem vencimento';

        const date = new Date(expiryDate);
        const now = new Date();
        const diffTime = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return `Vencido há ${Math.abs(diffDays)} dias`;
        } else if (diffDays === 0) {
            return 'Vence hoje';
        } else if (diffDays === 1) {
            return 'Vence amanhã';
        } else {
            return `Vence em ${diffDays} dias`;
        }
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidTime = (time) => {
        const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
        return timeRegex.test(time);
    };

    // Eventos dos steps
    const onStepChange = (stepIndex) => {
        console.log('Step changed to:', stepIndex);

        // Ao avançar para o Step 2 com múltiplos documentos, inicializar configurações
        if (stepIndex === 1 && hasMultipleDocuments.value) {
            selectedDocuments.value.forEach(docId => {
                initializeAlertConfig(docId);
            });
        }
    };

    const calculateUrgencyLevel = (expiryDate) => {
        if (!expiryDate) return 'safe';
        const date = new Date(expiryDate);
        const now = new Date();
        const diffTime = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 30) return 'critical';
        if (diffDays < 60) return 'warning';
        return 'safe';
    };

    const calculateNextNotification = (expiryDate, triggerDaysArray = null) => {
        const daysToUse = triggerDaysArray || triggerDays.value;
        if (!expiryDate || daysToUse.length === 0) return null;

        const expiryDateObj = new Date(expiryDate);
        const now = new Date();
        const maxTriggerDays = Math.max(...daysToUse);

        const nextNotificationDate = new Date(expiryDateObj.getTime() - (maxTriggerDays * 24 * 60 * 60 * 1000));

        if (nextNotificationDate <= now) {
            const remainingTriggers = daysToUse.filter(days => {
                const triggerDate = new Date(expiryDateObj.getTime() - (days * 24 * 60 * 60 * 1000));
                return triggerDate > now;
            });

            if (remainingTriggers.length > 0) {
                const nextTriggerDays = Math.max(...remainingTriggers);
                return new Date(expiryDateObj.getTime() - (nextTriggerDays * 24 * 60 * 60 * 1000)).toISOString();
            }
            return null;
        }

        return nextNotificationDate.toISOString();
    };

    const handleCreate = () => {
        const alertsData = selectedDocumentsData.value.map(document => {
            // Se houver múltiplos documentos, usar configurações individuais
            if (hasMultipleDocuments.value) {
                const config = alertConfigs.value[document.id];
                return {
                    name: config.name || document.name,
                    documentId: document.id,
                    documentType: document.type,
                    documentName: document.name,
                    expiryDate: document.expiryDate,
                    triggerDays: config.triggerDays,
                    recipients: emails.value,
                    preferredTime: config.preferredTime,
                    status: ALERT_STATUS.ACTIVE,
                    urgencyLevel: calculateUrgencyLevel(document.expiryDate),
                    nextNotification: calculateNextNotification(document.expiryDate, config.triggerDays),
                    lastNotification: null,
                    notificationChannels: ['email']
                };
            } else {
                // Usar configuração única
                return {
                    name: alertName.value || document.name,
                    documentId: document.id,
                    documentType: document.type,
                    documentName: document.name,
                    expiryDate: document.expiryDate,
                    triggerDays: triggerDays.value,
                    recipients: emails.value,
                    preferredTime: preferredTime.value,
                    status: ALERT_STATUS.ACTIVE,
                    urgencyLevel: calculateUrgencyLevel(document.expiryDate),
                    nextNotification: calculateNextNotification(document.expiryDate, triggerDays.value),
                    lastNotification: null,
                    notificationChannels: ['email']
                };
            }
        });

        emit('save', alertsData);
    };

    const handleCancel = () => {
        emit('close');
    };

    const loadAvailableDocuments = () => {
        availableDocuments.value = mockAvailableDocuments.filter(doc => doc.expiryDate);
        availableDocuments.value.sort((a, b) => {
            const dateA = new Date(a.expiryDate);
            const dateB = new Date(b.expiryDate);
            return dateA.getTime() - dateB.getTime();
        });
    };

    // Watchers
    watch(selectedDocuments, (newSelectedDocs) => {
        // Inicializar configurações para novos documentos selecionados
        if (hasMultipleDocuments.value) {
            newSelectedDocs.forEach(docId => {
                initializeAlertConfig(docId);
            });
        }
    }, { deep: true });

    // Lifecycle
    onMounted(() => {
        loadAvailableDocuments();
    });
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .newAlertModal {
        .step_content {
            display: flex;
            flex-direction: column;
            gap: 1.5em;
            padding: 1.5em 0;
        }

        .step_header {
            text-align: center;

            h4 {
                font-size: 1.375em;
                font-weight: 700;
                color: var(--textPrimary);
                margin: 0 0 0.5em 0;
            }

            p {
                font-size: 0.9375em;
                color: var(--textSecondary);
                margin: 0;
            }
        }

        .step_body {
            display: flex;
            flex-direction: column;
            gap: 1.5em;
        }

        .help_text {
            font-size: 0.8125em;
            color: var(--textSecondary);
            margin-top: 0.5em;
            margin-bottom: 0;
            text-align: center;
            font-style: italic;
        }

        .form_field {
            margin-bottom: 1.5em;

            &:last-child {
                margin-bottom: 0;
            }

            .field_label {
                display: block;
                font-size: 0.875em;
                font-weight: 600;
                color: var(--textPrimary);
                margin-bottom: 0.75em;
            }
        }

        .trigger_days {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
            gap: 0.75em;
            margin-bottom: 0.5em;

            &--compact {
                grid-template-columns: repeat(5, 1fr);
                gap: 0.5em;
            }

            .trigger_day {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 0.25em;
                padding: 1em 0.75em;
                border: 2px solid var(--borderColorLight);
                border-radius: 0.75em;
                cursor: pointer;
                transition: all 0.2s ease;
                background: white;

                &:hover {
                    border-color: rgba(59, 130, 246, 0.3);
                    transform: translateY(-1px);
                }

                &.active {
                    border-color: #3b82f6;
                    background: rgba(59, 130, 246, 0.05);

                    .day_number {
                        color: #3b82f6;
                    }
                }

                .day_number {
                    font-size: 1.25em;
                    font-weight: 700;
                    color: var(--textPrimary);
                    transition: color 0.2s ease;
                }

                .day_label {
                    font-size: 0.75em;
                    color: var(--textSecondary);
                    font-weight: 500;
                }
            }
        }

        .document_config_card {
            border: 1px solid var(--borderColorLight);
            border-radius: 0.75em;
            margin-bottom: 1.5em;
            overflow: hidden;

            &:last-child {
                margin-bottom: 0;
            }

            .document_config_header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 1em 1.25em;
                background: #f9fafb;
                border-bottom: 1px solid var(--borderColorLight);

                .document_config_title {
                    font-size: 0.9375em;
                    font-weight: 600;
                    color: var(--textPrimary);
                }

                .document_config_expiry {
                    font-size: 0.8125em;
                    font-weight: 500;
                    color: var(--textSecondary);
                    background: white;
                    padding: 0.25em 0.75em;
                    border-radius: 0.5em;
                    border: 1px solid var(--borderColorLight);
                }
            }

            .document_config_body {
                padding: 1.25em;
                display: flex;
                flex-direction: column;
                gap: 1.25em;
                background: white;

                .config_field {
                    .field_label {
                        display: block;
                        font-size: 0.875em;
                        font-weight: 600;
                        color: var(--textPrimary);
                        margin-bottom: 0.75em;
                    }
                }
            }
        }

        .multiple_alerts_warning {
            display: flex;
            gap: 0.75em;
            background: rgba(59, 130, 246, 0.05);
            border: 1px solid rgba(59, 130, 246, 0.2);
            border-radius: 0.75em;
            padding: 1em 1.25em;

            .warning_icon {
                flex-shrink: 0;

                :deep(svg) {
                    width: 1.5em;
                    height: 1.5em;
                    color: #3b82f6;
                }
            }

            .warning_content {
                font-size: 0.875em;
                color: var(--textPrimary);
                line-height: 1.5;

                strong {
                    color: #3b82f6;
                }
            }
        }

        .alert_summary_card {
            background: white;
            border: 1px solid var(--borderColorLight);
            border-radius: 0.75em;
            margin-bottom: 1em;
            overflow: hidden;

            &:last-child {
                margin-bottom: 1em;
            }

            .alert_summary_header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0.875em 1em;
                background: #f9fafb;
                border-bottom: 1px solid var(--borderColorLight);

                .alert_summary_title {
                    font-size: 0.875em;
                    font-weight: 600;
                    color: var(--textPrimary);
                }

                .alert_summary_expiry {
                    font-size: 0.75em;
                    font-weight: 500;
                    color: var(--textSecondary);
                    background: white;
                    padding: 0.25em 0.625em;
                    border-radius: 0.375em;
                    border: 1px solid var(--borderColorLight);
                }
            }

            .alert_summary_body {
                padding: 1.25em;
                display: flex;
                flex-direction: column;
                gap: 1em;
                background: white;

                .alert_detail_item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5em;

                    .alert_detail_label {
                        display: flex;
                        align-items: center;
                        gap: 0.5em;
                        font-size: 0.75em;
                        font-weight: 600;
                        color: var(--textSecondary);
                        text-transform: uppercase;
                        letter-spacing: 0.025em;

                        :deep(svg) {
                            width: 1em;
                            height: 1em;
                            color: #6b7280;
                        }
                    }

                    .alert_detail_value {
                        font-size: 0.9375em;
                        font-weight: 500;
                        color: var(--textPrimary);
                        padding-left: 1.5em;
                    }
                }
            }
        }

        .summary_title {
            font-size: 1em;
            font-weight: 700;
            color: var(--textPrimary);
            margin: 0 0 1em 0;
        }

        .summary_content {
            background: rgba(16, 185, 129, 0.05);
            border: 1px solid rgba(16, 185, 129, 0.1);
            border-radius: 0.75em;
            padding: 1.25em;

            &--emails {
                background: rgba(99, 102, 241, 0.05);
                border-color: rgba(99, 102, 241, 0.1);
                margin-top: 0.5em;
            }

            .summary_item {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 0.75em;

                &:last-child {
                    margin-bottom: 0;
                }

                &--compact {
                    margin-bottom: 0.5em;

                    &:last-child {
                        margin-bottom: 0;
                    }
                }

                .summary_label {
                    font-size: 0.875em;
                    color: var(--textSecondary);
                    font-weight: 500;
                    min-width: 7em;
                }

                .summary_value {
                    font-size: 0.875em;
                    color: var(--textPrimary);
                    font-weight: 600;
                    text-align: right;
                    flex: 1;

                    .summary_document {
                        font-size: 0.8125em;
                        font-weight: 500;
                        margin-bottom: 0.25em;

                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }

        .step_actions {
            display: flex;
            gap: 1em;
            justify-content: flex-end;
            padding-top: 1.5em;
            border-top: 1px solid var(--borderColorLight);
            margin-top: 1.5em;
        }
    }

    @include screen_medias(s) {
        .newAlertModal {
            .trigger_days {
                grid-template-columns: repeat(3, 1fr);

                &--compact {
                    grid-template-columns: repeat(3, 1fr);
                }

                .trigger_day {
                    padding: 0.75em 0.5em;

                    .day_number {
                        font-size: 1.125em;
                    }
                }
            }

            .document_config_card {
                .document_config_header {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.5em;

                    .document_config_expiry {
                        align-self: flex-end;
                    }
                }
            }

            .alert_summary_card {
                .alert_summary_header {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.5em;

                    .alert_summary_expiry {
                        align-self: flex-end;
                    }
                }
            }

            .multiple_alerts_warning {
                flex-direction: column;
                gap: 0.5em;

                .warning_icon {
                    :deep(svg) {
                        width: 1.25em;
                        height: 1.25em;
                    }
                }
            }

            .step_actions {
                flex-direction: column;
                gap: 0.75em;
            }

            .summary_content {
                .summary_item {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.25em;

                    &--compact {
                        flex-direction: row;
                        gap: 0;
                    }

                    .summary_value {
                        text-align: left;
                    }
                }
            }
        }
    }
</style>